import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, ExternalLink, Loader2, Search } from 'lucide-react';
import { listIntegrations, listPersonalIntegrations, getIntegrationResources, addProjectLink, removeProjectLink } from '../lib/api';
import { ProviderMark } from './ProviderMark';
import { queryKeys } from '../lib/queryKeys';
import { useUserStore } from '../store/userStore';
import type { Project, ProjectLink, IntegrationResource } from '../types';

const PROVIDER_LABELS: Record<string, string> = {
  jira: 'Jira',
  linear: 'Linear',
  slack: 'Slack',
  google: 'Google',
};

function AddLinkModal({
  provider,
  projectId,
  existingIds,
  onClose,
}: {
  provider: string;
  projectId: string;
  existingIds: Set<string>;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [selected, setSelected] = useState<IntegrationResource | null>(null);
  const [filter, setFilter] = useState('');
  const isGoogle = provider === 'google';

  // Google's resource list is a live Drive search (folders by default, any
  // name match once the caller types) rather than a small fixed directory —
  // re-fetch as the filter changes instead of filtering a single cached
  // page client-side, which is what Jira/Linear/Slack do below.
  const { data: resources, isLoading } = useQuery({
    queryKey: isGoogle ? queryKeys.integrationResources(`google:${filter}`) : queryKeys.integrationResources(provider),
    queryFn: () => getIntegrationResources(provider, isGoogle ? filter : undefined),
  });

  const addMutation = useMutation({
    mutationFn: (resource: IntegrationResource) =>
      addProjectLink(projectId, {
        provider,
        externalResourceId: resource.id,
        displayName: resource.name,
        webUrl: resource.url,
        syncEnabled: true,
        resourceType: resource.extra?.resourceType,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.project(projectId) });
      onClose();
    },
  });

  const filtered = (resources ?? [])
    .filter((r) => !existingIds.has(r.id))
    .filter((r) => isGoogle || r.name.toLowerCase().includes(filter.toLowerCase()));

  // Slack extra.key is the internal channel ID — not useful to display.
  // For Jira/Linear the key is the project/team short code which is useful.
  const showKey = provider !== 'slack' && !isGoogle;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-bg-surface border border-gray-200 dark:border-white/10 rounded-2xl w-full max-w-sm p-5 space-y-4 shadow-xl">
        <div className="flex items-center gap-3">
          <ProviderMark provider={provider} className="h-5 w-5 flex-shrink-0" />
          <h3 className="font-semibold text-gray-900 dark:text-white text-meta">
            Link {PROVIDER_LABELS[provider]} to project
          </h3>
        </div>

        {/* Filter input — for Google this drives a live Drive search
            (folders by default, any name match once typed); for the other
            providers it filters the single fetched resource list client-side. */}
        {(isGoogle || (!isLoading && (resources?.length ?? 0) > 5)) && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={isGoogle ? 'Search Drive by name…' : `Filter ${PROVIDER_LABELS[provider]} resources…`}
              autoFocus
              className="w-full pl-8 pr-3 py-2 rounded-lg bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white text-meta placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/30"
            />
          </div>
        )}
        {isGoogle && !filter && (
          <p className="text-micro text-gray-500 dark:text-gray-500">
            Showing folders. Search by name to link a specific file instead.
          </p>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
          </div>
        ) : !resources?.length ? (
          <p className="text-meta text-gray-500 dark:text-gray-400 py-4 text-center">
            No {PROVIDER_LABELS[provider]} resources found.
          </p>
        ) : filtered.length === 0 ? (
          <p className="text-meta text-gray-500 dark:text-gray-400 py-4 text-center">
            No results for "{filter}"
          </p>
        ) : (
          <div className="max-h-64 overflow-y-auto space-y-0.5 -mx-1 px-1">
            {filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => setSelected(r)}
                className={`w-full text-left px-3 py-2.5 rounded-lg text-meta transition-colors flex items-center justify-between gap-2 ${
                  selected?.id === r.id
                    ? 'bg-primary/10 dark:bg-primary/20 text-primary border border-primary/30'
                    : 'hover:bg-gray-100 dark:hover:bg-white/5 text-gray-900 dark:text-white border border-transparent'
                }`}
              >
                <span className="font-medium truncate">{r.name}</span>
                {showKey && r.extra?.key && (
                  <span className="text-micro text-gray-400 dark:text-gray-500 flex-shrink-0 font-mono">
                    {r.extra.key}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {addMutation.isError && (
          <p className="text-micro text-red-500 dark:text-red-400">
            {(addMutation.error as Error).message}
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 rounded-lg text-meta text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => selected && addMutation.mutate(selected)}
            disabled={!selected || addMutation.isPending}
            className="flex-1 px-3 py-2 rounded-lg text-meta bg-primary text-white hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {addMutation.isPending ? 'Linking\u2026' : 'Link'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function ProjectLinks({ project }: { project: Project }) {
  const qc = useQueryClient();
  const { sessionConfig } = useUserStore();
  const [addingProvider, setAddingProvider] = useState<string | null>(null);

  const canEdit =
    sessionConfig?.entityRole === 'admin' ||
    sessionConfig?.functionalRoles?.includes('product_manager') ||
    sessionConfig?.role === 'admin' ||
    sessionConfig?.role === 'product_manager';

  const { data: integrations } = useQuery({
    queryKey: queryKeys.integrations,
    queryFn: listIntegrations,
  });

  // Google is per-user (see routers/personal_integrations.py) — whether
  // *this* caller can link a Drive folder/file depends on their OWN
  // personal connection, not the entity-level `integrations` list above,
  // which never reflects a per-user Google grant.
  const { data: personalIntegrations } = useQuery({
    queryKey: queryKeys.personalIntegrations,
    queryFn: listPersonalIntegrations,
  });
  const personalGoogleConnected = (personalIntegrations ?? []).some(
    (i) => i.provider === 'google' && i.isConnected,
  );

  const removeMutation = useMutation({
    mutationFn: (linkId: string) => removeProjectLink(project.projectId, linkId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.project(project.projectId) });
    },
  });

  const connectedProviders = (integrations ?? [])
    .filter((i) => i.isConnected || (i as { status?: string }).status === 'connected')
    .map((i) => i.provider);
  if (personalGoogleConnected) connectedProviders.push('google');

  const links: ProjectLink[] = project.links ?? [];

  const LINKABLE = new Set(['jira', 'linear', 'slack', 'google']);
  const linkableProviders = connectedProviders.filter((p) => LINKABLE.has(p));

  if (!canEdit && links.length === 0) return null;

  return (
    <>
      {addingProvider && (
        <AddLinkModal
          provider={addingProvider}
          projectId={project.projectId}
          existingIds={new Set(links.filter((l) => l.provider === addingProvider).map((l) => l.externalResourceId))}
          onClose={() => setAddingProvider(null)}
        />
      )}

      <div className="glass-card rounded-xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
            Connected Sources
          </h3>
        </div>

        {links.length === 0 && canEdit ? (
          <p className="text-meta text-gray-500 dark:text-gray-500">
            Link Jira, Linear, Slack, or a Google Drive folder to pull signals into this project.
          </p>
        ) : null}

        {links.length > 0 && (
          <div className="space-y-4">
            {links.map((link) => (
              <div
                key={link.linkId}
                className="flex items-center gap-2.5 group"
              >
                <ProviderMark provider={link.provider} className="h-4 w-4 flex-shrink-0" />
                <span className="text-body text-gray-700 dark:text-gray-300 flex-1 truncate">
                  {link.displayName}
                  {link.autoLinked && (
                    <span className="ml-1.5 text-meta text-gray-500 dark:text-gray-600">(auto)</span>
                  )}
                </span>
                {link.webUrl && (
                  <a
                    href={link.webUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opacity-0 group-hover:opacity-100 text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-all"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}
                {canEdit && (
                  <button
                    onClick={() => removeMutation.mutate(link.linkId)}
                    disabled={removeMutation.isPending}
                    className="opacity-0 group-hover:opacity-100 text-gray-500 dark:text-gray-600 hover:text-red-400 transition-all"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {canEdit && linkableProviders.length > 0 && (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {linkableProviders.map((provider) => (
              <button
                key={provider}
                onClick={() => setAddingProvider(provider)}
                className="flex items-center gap-1 text-meta px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white border border-gray-200 dark:border-white/10 transition-colors focus-visible:ring-2 focus-visible:ring-primary/50"
              >
                <Plus className="h-4 w-4" />
                {PROVIDER_LABELS[provider]}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
